/**
 * Change an animal's taxon. The tree is the image's own `taxa` list (never
 * hard-coded: it grows with the analysis), and any level can be picked, not
 * just the leaves — "hexapoda" means "a hexapod, but I can't tell which".
 * Saving writes one correction; the backend records who and when.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2Icon } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    FaunaAnimal,
    FaunaTaxon,
    taxonLabel,
    useSaveFaunaEdit,
} from '@/services/fauna.service';

type Row = { taxon: FaunaTaxon; depth: number };

/** The taxa as a depth-first list, each child under its parent. */
function flattenTree(taxa: FaunaTaxon[]): Row[] {
    const codes = new Set(taxa.map((t) => t.code));
    const children = new Map<string | null, FaunaTaxon[]>();
    for (const taxon of taxa) {
        // A parent missing from the list would orphan the branch: hang it at
        // the root instead of losing it.
        const parent =
            taxon.parent && codes.has(taxon.parent) ? taxon.parent : null;
        children.set(parent, [...(children.get(parent) ?? []), taxon]);
    }
    const rows: Row[] = [];
    const visit = (parent: string | null, depth: number) => {
        for (const taxon of children.get(parent) ?? []) {
            rows.push({ taxon, depth });
            visit(taxon.code, depth + 1);
        }
    };
    visit(null, 0);
    return rows;
}

export default function TaxonDialog({
    projectId,
    imageId,
    animal,
    taxa,
    onClose,
}: {
    projectId?: string;
    imageId?: string;
    animal: FaunaAnimal;
    taxa: FaunaTaxon[];
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const [taxon, setTaxon] = useState(animal.taxon);
    const [note, setNote] = useState(animal.taxon_note ?? '');
    const [filter, setFilter] = useState('');
    const save = useSaveFaunaEdit(projectId, imageId);

    const rows = useMemo(() => flattenTree(taxa), [taxa]);
    const query = filter.trim().toLowerCase();
    const shown = query
        ? rows.filter(
              ({ taxon: x }) =>
                  x.code.toLowerCase().includes(query) ||
                  x.name?.toLowerCase().includes(query),
          )
        : rows;

    const trimmedNote = note.trim();
    const changed =
        taxon !== animal.taxon || trimmedNote !== (animal.taxon_note ?? '');

    const onSave = () => {
        save.mutate(
            [
                {
                    action: 'retaxon',
                    id: animal.id,
                    bbox: animal.bbox,
                    taxon,
                    taxon_note: trimmedNote || null,
                },
            ],
            { onSuccess: onClose },
        );
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{t('Change taxon')}</DialogTitle>
                </DialogHeader>

                <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={t('Search a taxon')}
                    autoFocus
                />
                <div
                    role="listbox"
                    aria-label={t('Taxa')}
                    className="max-h-72 overflow-y-auto rounded-md border border-[#dadbd5] py-1"
                >
                    {shown.map(({ taxon: x, depth }) => {
                        const active = x.code === taxon;
                        return (
                            <button
                                key={x.code}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => setTaxon(x.code)}
                                style={{
                                    paddingLeft: `${(query ? 0 : depth) * 16 + 10}px`,
                                }}
                                className={`flex w-full items-baseline gap-2 py-1 pr-2 text-left text-sm ${
                                    active
                                        ? 'bg-[#2d6a4f] text-white'
                                        : 'text-[#1c2620] hover:bg-[#f4f5f1]'
                                }`}
                            >
                                <span className="italic">
                                    {taxonLabel(x.code)}
                                </span>
                                {x.name && (
                                    <span
                                        className={`truncate text-xs ${
                                            active
                                                ? 'text-white/75'
                                                : 'text-[#7a8077]'
                                        }`}
                                    >
                                        {x.name}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {shown.length === 0 && (
                        <p className="px-3 py-4 text-center text-xs text-[#7a8077]">
                            {t('No taxon matches')}
                        </p>
                    )}
                </div>

                <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('Note (optional)')}
                    maxLength={500}
                    rows={2}
                />

                {save.isError && (
                    <p className="text-sm text-red-600">
                        {t('Not saved, try again.')}
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={onSave}
                        disabled={!changed || save.isPending}
                        className="bg-[#2d6a4f] text-white hover:bg-[#245741]"
                    >
                        {save.isPending && (
                            <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" />
                        )}
                        {t('Save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
